import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { FileText, Library, Plus } from 'lucide-react';
import { ReportTextLibrary } from '@/components/patientReports/ReportTextLibrary';
import { ReportToolColumn } from '@/components/patientReports/ReportToolColumn';
import { ReportToolTabs, type ReportToolTab } from '@/components/patientReports/ReportToolTabs';
import { useReportWorkspace } from '@/components/patientReports/useReportWorkspace';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { StatusChip } from '@/components/ui/Chip';
import { ReportPreview } from '@/components/patientReports/ReportPreview';
import { ReportPreviewFrame } from '@/components/patientReports/ReportPreviewFrame';
import { ReportSectionCards } from '@/components/patientReports/ReportSectionCards';
import { WordReportPreview } from '@/components/patientReports/WordReportPreview';
import { useClinic } from '@/hooks/useClinicSettings';
import { loadFormsAssignmentDetail } from '@/hooks/useFormsAssignmentResponses';
import { useFormsTemplateList } from '@/hooks/useFormsTemplates';
import {
  downloadPatientReportPdf,
  loadPatientReportPdfBlob,
  useDeletePatientReport,
  useFinishPatientReport,
  usePatientReportList,
  usePatientReportSettings,
  usePatientReportSources,
  usePatientReportTemplates,
  useSavePatientReport,
} from '@/hooks/usePatientReports';
import {
  patientReportTextErrorMessage,
  usePatientReportTextLibrary,
} from '@/hooks/usePatientReportTexts';
import { useAuth } from '@/lib/auth';
import { revealReportCpr } from '@/lib/patientReports/cpr';
import {
  downloadStoredDocx,
  loadReportAuthorName,
  saveDocxFile,
} from '@/lib/patientReports/docxFile';
import { docxHasToken, fillDocxTemplate } from '@/lib/patientReports/docxTemplate';
import {
  CPR_PLACEHOLDER_TOKENS,
  buildPlaceholderDictionary,
  placeholderSectionTexts,
  reportPlaceholderContext,
} from '@/lib/patientReports/placeholders';
import { materializeReportPdf } from '@/lib/patientReports/reportDocument';
import { EMPTY_REPORT_CATALOG, type PatientReportRow } from '@/lib/patientReports/model';
import {
  insertUserTextSection,
  matchedQuestionnaire,
  missingQuestionnaireEntries,
  previewToPdfBlocks,
  questionnaireAssignmentIds,
  questionnairePreviewFromDetail,
  reportSaveSelections,
  resolveLetterheadMode,
  sectionsFromStored,
  templateReportName,
  type QuestionnairePreviewDetail,
  type ReportPreviewLabels,
  type ReportSection,
} from '@/lib/patientReports/template';
import {
  personalTextBodies,
  personalTextNeedsCpr,
  resolvePreviewWithPersonalTexts,
} from '@/lib/patientReports/userTexts';
import type {
  PatientReportLetterheadChoice,
  PatientReportLetterheadMode,
} from '@/lib/patientReports/model';
import { REPORT_TOOL_WIDTH_MIN } from '@/lib/patientReports/workspacePrefs';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export function PatientReportsTab({
  clinicId,
  patientId,
  patientName: _patientName,
  listRequest = 0,
}: {
  clinicId: string;
  patientId: string;
  /** Kept for callers. The report title does not copy the name. */
  patientName: string;
  /** Bumps when the Rapport tab is chosen while this tab is already open. */
  listRequest?: number;
}) {
  const { t } = useTranslation();
  const list = usePatientReportList(clinicId, patientId, true);
  const removeDraft = useDeletePatientReport(clinicId, patientId);
  const [open, setOpen] = useState<PatientReportRow | 'new' | null>(null);
  const leaveRef = useRef<(() => void) | null>(null);
  const listRequestSeen = useRef(listRequest);
  const registerLeave = useCallback((fn: (() => void) | null) => {
    leaveRef.current = fn;
  }, []);

  useEffect(() => {
    if (listRequestSeen.current === listRequest) return;
    listRequestSeen.current = listRequest;
    leaveRef.current?.();
  }, [listRequest]);

  if (list.isLoading) {
    return <p className={cn(TYPE.body, 'text-ds-muted')}>{t('patientReports.loading')}</p>;
  }
  if (list.error) {
    return <ErrorAlert message={t('patientReports.load_error')} />;
  }

  if (open) {
    const initial = open === 'new' ? null : open;
    return (
      <ReportEditor
        key={initial?.id ?? 'new'}
        clinicId={clinicId}
        patientId={patientId}
        initial={initial}
        onClose={() => setOpen(null)}
        registerLeave={registerLeave}
      />
    );
  }

  const rows = list.data ?? [];
  return (
    <div className="flex flex-col gap-s4">
      <div className="flex flex-wrap items-center justify-between gap-s3">
        <div>
          <h2 className={cn(TYPE.heading, 'text-ds-text')}>{t('patients.tabs.patient_reports')}</h2>
          <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>{t('patientReports.list_hint')}</p>
        </div>
        <Button onClick={() => setOpen('new')}>
          <Plus className="h-4 w-4" />
          {t('patientReports.new')}
        </Button>
      </div>
      {rows.length === 0 ? (
        <EmptyState
          icon={<FileText className="h-7 w-7" />}
          title={t('patientReports.empty')}
          description={t('patientReports.empty_hint')}
        />
      ) : (
        <ul className="divide-y divide-ds-line rounded-ctl border border-ds-line bg-ds-card">
          {rows.map((row) => (
            <li key={row.id} className="flex flex-wrap items-center gap-s3 px-s4 py-s3">
              <button
                type="button"
                className="min-w-0 flex-1 text-left"
                onClick={() => setOpen(row)}
              >
                <span className={cn(TYPE.body, 'font-medium text-ds-text')}>{row.title}</span>
                <span className={cn(TYPE.small, 'mt-s1 block text-ds-muted')}>
                  {row.updatedAt ? new Date(row.updatedAt).toLocaleString('da-DK') : ''}
                </span>
              </button>
              <StatusChip tone={row.status === 'finished' ? 'completed' : 'neutral'}>
                {row.status === 'finished'
                  ? t('patientReports.status_finished')
                  : t('patientReports.status_draft')}
              </StatusChip>
              {row.status === 'draft' ? (
                <Button
                  variant="tertiary"
                  loading={removeDraft.isPending}
                  onClick={() => {
                    if (!window.confirm(t('patientReports.delete_confirm'))) return;
                    void removeDraft.mutateAsync(row.id).then(
                      () => toast.success(t('patientReports.deleted')),
                      () => toast.error(t('patientReports.delete_error')),
                    );
                  }}
                >
                  {t('patientReports.delete_draft')}
                </Button>
              ) : null}
              {row.pdfStoragePath ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    void downloadPatientReportPdf(row.pdfStoragePath!, `${row.title}.pdf`).catch(
                      () => {
                        toast.error(t('patientReports.download_error'));
                      },
                    );
                  }}
                >
                  {t('patientReports.download')}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReportEditor({
  clinicId,
  patientId,
  initial,
  onClose,
  registerLeave,
}: {
  clinicId: string;
  patientId: string;
  initial: PatientReportRow | null;
  onClose: () => void;
  registerLeave: (fn: (() => void) | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const { hasPermission, user } = useAuth();
  const tools = useReportWorkspace(user?.id);
  const previewText = useMemo(() => previewLabels(t), [t]);
  const catalogQuery = usePatientReportSources(clinicId, patientId, true);
  const templates = usePatientReportTemplates(clinicId);
  const settings = usePatientReportSettings(clinicId);
  const clinic = useClinic(clinicId);
  const save = useSavePatientReport(clinicId, patientId);
  const finish = useFinishPatientReport(clinicId, patientId);
  const removeDraft = useDeletePatientReport(clinicId, patientId);
  const catalog = catalogQuery.data ?? EMPTY_REPORT_CATALOG;
  const library = usePatientReportTextLibrary(clinicId);
  const personalBodies = useMemo(() => personalTextBodies(library.texts), [library.texts]);
  const [authorName, setAuthorName] = useState('');
  const forms = useFormsTemplateList(clinicId, i18n.language);
  const formOptions = useMemo(() => {
    const rows = [...(forms.data?.clinic ?? []), ...(forms.data?.platform ?? [])];
    const seen = new Set<string>();
    const options: { id: string; name: string }[] = [];
    for (const row of rows) {
      if (!row.id || seen.has(row.id)) continue;
      seen.add(row.id);
      options.push({
        id: row.id,
        name: row.display_name_da || row.display_name_en || row.id,
      });
    }
    return options;
  }, [forms.data]);

  const [reportId, setReportId] = useState<string | null>(initial?.id ?? null);
  const [title, setTitle] = useState(initial?.title ?? defaultTitle());
  const [templateId, setTemplateId] = useState<string | null>(initial?.templateId ?? null);
  const [letterhead, setLetterhead] = useState<PatientReportLetterheadChoice>(
    initial?.letterhead ?? 'inherit',
  );
  const [includeCover, setIncludeCover] = useState(initial?.includeCover ?? false);
  const [coverTitle, setCoverTitle] = useState(initial?.coverTitle ?? '');
  const [coverBody, setCoverBody] = useState(initial?.coverBody ?? '');
  const [sections, setSections] = useState<ReportSection[]>(() =>
    sectionsFromStored(initial?.composition ?? [], initial?.blocks ?? []),
  );
  const [status, setStatus] = useState<'draft' | 'finished'>(initial?.status ?? 'draft');
  const [pdfPath, setPdfPath] = useState<string | null>(initial?.pdfStoragePath ?? null);
  const [pdfStale, setPdfStale] = useState(false);
  const [details, setDetails] = useState<Record<string, QuestionnairePreviewDetail>>({});
  const [cpr, setCpr] = useState<string | null>(null);
  const [cprAccess, setCprAccess] = useState<'hidden' | 'loading' | 'ready'>('hidden');
  const [cprError, setCprError] = useState(false);
  const [wordBusy, setWordBusy] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [toolTab, setToolTab] = useState<ReportToolTab>('content');
  const activeTemplate = templates.data?.templates.find((item) => item.id === templateId) ?? null;
  const wordTemplatePath =
    activeTemplate?.docxStoragePath || settings.data?.docxTemplateStoragePath || null;
  const locked = status === 'finished';
  const reportName = title.trim();
  const snapshot = reportEditorSnapshot({
    title,
    templateId,
    letterhead,
    includeCover,
    coverTitle,
    coverBody,
    sections,
  });
  const [baseline, setBaseline] = useState(snapshot);
  const dirty = !locked && snapshot !== baseline;

  const markEdited = () => {
    if (status === 'finished') setPdfStale(true);
  };
  const needsCpr =
    sections.some(
      (section) => section.kind === 'patient_fields' && section.fields.includes('cpr'),
    ) || personalTextNeedsCpr(sections, personalBodies);

  useEffect(() => {
    let cancelled = false;
    void loadReportAuthorName()
      .then((name) => {
        if (!cancelled && name) setAuthorName(name);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  const canRevealCpr = hasPermission(clinicId, 'patients_cpr', 'view');
  const assignmentKey = questionnaireAssignmentIds(sections, catalog).join('|');

  useEffect(() => {
    if (!assignmentKey) return;
    let cancelled = false;
    void Promise.all(
      assignmentKey.split('|').map(async (id) => {
        const source = catalog.questionnaires.find((item) => item.id === id);
        if (!source?.formsAssignmentId) return [id, null] as const;
        try {
          const detail = await loadFormsAssignmentDetail(clinicId, source.formsAssignmentId);
          return [
            id,
            questionnairePreviewFromDetail({
              questions: detail.questionnaire?.questions ?? [],
              responses: detail.responses,
              scoreDetails: detail.score_details,
              scoringConfig: detail.questionnaire?.scoring_config ?? null,
              templateName: source.name,
              language: i18n.language,
            }),
          ] as const;
        } catch {
          return [id, null] as const;
        }
      }),
    ).then((rows) => {
      if (cancelled) return;
      const next: Record<string, QuestionnairePreviewDetail> = {};
      for (const [id, detail] of rows) {
        if (detail) next[id] = detail;
      }
      setDetails(next);
    });
    return () => {
      cancelled = true;
    };
  }, [assignmentKey, catalog.questionnaires, clinicId, i18n.language]);

  useEffect(() => {
    if (!needsCpr) {
      setCpr(null);
      setCprError(false);
      setCprAccess('ready');
      return;
    }
    if (!canRevealCpr) {
      setCpr(null);
      setCprError(false);
      setCprAccess('hidden');
      return;
    }
    let cancelled = false;
    setCprAccess('loading');
    setCprError(false);
    void revealReportCpr(patientId)
      .then((value) => {
        if (cancelled) return;
        setCpr(value);
        setCprAccess('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setCpr(null);
        setCprError(true);
        setCprAccess('ready');
      });
    return () => {
      cancelled = true;
      setCpr(null);
    };
  }, [canRevealCpr, needsCpr, patientId]);

  const questions = useMemo(() => {
    const map: Record<string, { id: string; text: string }[]> = {};
    for (const section of sections) {
      if (section.kind !== 'questionnaire') continue;
      const match = matchedQuestionnaire(section, catalog);
      const detail = match ? details[match.id] : undefined;
      if (!detail) continue;
      map[section.id] = detail.answers.map((answer) => ({ id: answer.id, text: answer.prompt }));
    }
    return map;
  }, [catalog, details, sections]);

  const resolvedLetterhead = resolveLetterheadMode(letterhead, settings.data?.letterhead);
  const previewOf = (
    showEmpty: boolean,
    access: typeof cprAccess = cprAccess,
    cprValue: string | null = cpr,
    previewTitle: string = title,
  ) =>
    resolvePreviewWithPersonalTexts(
      {
        title: previewTitle,
        includeCover,
        coverTitle,
        coverBody,
        letterhead,
        clinicLetterhead: settings.data?.letterhead,
        sections,
        catalog,
        cprAccess: access,
        cpr: cprValue,
        details,
        labels: previewText,
        showEmpty,
      },
      {
        bodies: personalBodies,
        authorName,
        clinicName: clinic.data?.name ?? '',
        generatedAt: new Date(),
        missingText: t('patientReports.user_text_missing'),
        chooseText: t('patientReports.user_text_choose'),
      },
    );
  const preview = previewOf(true);

  const persist = async () => {
    const payload = reportSaveSelections({
      patientId,
      templateId,
      letterhead,
      includeCover,
      coverTitle,
      coverBody,
      sections,
    });
    const row = await save.mutateAsync({
      reportId,
      title: reportName,
      ...payload,
    });
    setReportId(row.id);
    setStatus(row.status);
    setPdfPath(row.pdfStoragePath);
    return row;
  };

  const onSave = async (): Promise<boolean> => {
    if (locked) return false;
    if (!reportName) {
      toast.error(t('patientReports.title_required'));
      return false;
    }
    const saved = snapshot;
    try {
      await persist();
      setBaseline(saved);
      toast.success(t('patientReports.saved'));
      return true;
    } catch {
      toast.error(t('patientReports.save_error'));
      return false;
    }
  };

  const onFinish = async () => {
    if (locked) return;
    if (!reportName) {
      toast.error(t('patientReports.title_required'));
      return;
    }
    if (needsCpr && canRevealCpr && (cprError || cprAccess === 'loading' || !cpr)) {
      toast.error(t('patientReports.cpr_error'));
      return;
    }
    const saved = snapshot;
    try {
      const row = await persist();
      setBaseline(saved);
      const pdfPreview = previewOf(false, cprAccess, cpr, reportName);
      const pdf = await materializeReportPdf({
        settings: settings.data ?? {
          enabled: true,
          letterhead: 'none',
          letterheadStoragePath: null,
          letterheadMime: null,
          docxTemplateStoragePath: null,
        },
        clinic: clinic.data,
        letterhead,
        wordPath: wordTemplatePath,
        coverPath: includeCover ? (activeTemplate?.coverStoragePath ?? null) : null,
        coverMime: activeTemplate?.coverMime ?? null,
        includeCover,
        placeholders: reportPlaceholderContext({
          reportTitle: reportName,
          authorName: await loadReportAuthorName(),
          clinicName: clinic.data?.name ?? '',
          generatedAt: new Date(),
          patient: catalog.patient,
          cprAccess,
          cpr,
          cprHidden: previewText.cprHidden,
          cprLoading: previewText.cprLoading,
          coverTitle,
          coverBody,
          sections,
          model: pdfPreview,
        }),
        title: reportName,
        blocks: previewToPdfBlocks(pdfPreview),
        patientId,
        canRevealCpr,
      });
      const finished = await finish.mutateAsync({ reportId: row.id, pdf });
      setStatus(finished.status);
      setPdfPath(finished.pdfStoragePath);
      setPdfStale(false);
      toast.success(t('patientReports.finished'));
    } catch {
      toast.error(t('patientReports.finish_error'));
    }
  };

  const onDownloadWord = async () => {
    if (!wordTemplatePath) return;
    if (!reportName) {
      toast.error(t('patientReports.title_required'));
      return;
    }
    setWordBusy(true);
    try {
      const templateBytes = await downloadStoredDocx(wordTemplatePath);
      const wantsCpr = CPR_PLACEHOLDER_TOKENS.some((token) => docxHasToken(templateBytes, token));
      let cprValue = cpr;
      let access = cprAccess;
      if (wantsCpr) {
        if (!canRevealCpr) {
          access = 'hidden';
          cprValue = null;
        } else if (!cprValue) {
          try {
            cprValue = await revealReportCpr(patientId);
            access = 'ready';
          } catch {
            toast.error(t('patientReports.cpr_error'));
            return;
          }
        } else {
          access = 'ready';
        }
      }
      const wordPreview = previewOf(false, access, cprValue, reportName);
      const filled = fillDocxTemplate(
        templateBytes,
        buildPlaceholderDictionary({
          reportTitle: reportName,
          authorName: await loadReportAuthorName(),
          clinicName: clinic.data?.name ?? '',
          generatedAt: new Date(),
          patient: catalog.patient,
          cprAccess: access,
          cpr: cprValue,
          cprHidden: t('patientReports.cpr_hidden'),
          cprLoading: t('patientReports.cpr_loading'),
          coverTitle,
          coverBody,
          ...placeholderSectionTexts(sections, wordPreview),
        }),
      );
      saveDocxFile(filled, `${reportName}.docx`);
      toast.success(t('patientReports.download_word_done'));
    } catch {
      toast.error(t('patientReports.download_word_error'));
    } finally {
      setWordBusy(false);
    }
  };

  const applyTemplate = (value: string) => {
    if (!value) {
      setTemplateId(null);
      markEdited();
      return;
    }
    const template = templates.data?.templates.find((item) => item.id === value);
    if (!template) return;
    if (sections.length > 0 && !window.confirm(t('patientReports.template_replace'))) return;
    setTemplateId(template.id);
    setTitle(templateReportName(template));
    setSections(template.sections);
    setLetterhead(template.letterhead);
    setIncludeCover(template.includeCover);
    setCoverTitle(template.coverTitle);
    setCoverBody(template.coverBody);
    markEdited();
  };

  const requestLeave = () => {
    if (dirty && !locked) {
      setLeaveOpen(true);
      return;
    }
    onClose();
  };
  const requestLeaveRef = useRef(requestLeave);
  requestLeaveRef.current = requestLeave;
  useEffect(() => {
    registerLeave(() => requestLeaveRef.current());
    return () => registerLeave(null);
  }, [registerLeave]);

  const saveAndLeave = async () => {
    const ok = await onSave();
    if (!ok) return;
    setLeaveOpen(false);
    onClose();
  };

  const busy = save.isPending || finish.isPending || wordBusy;
  const reportSettings = (
    <ReportEditorSettings
      locked={locked}
      title={reportName}
      templates={templates.data?.templates ?? []}
      templateId={templateId}
      onTemplateChange={applyTemplate}
      letterhead={letterhead}
      onLetterheadChange={(value) => {
        setLetterhead(value);
        markEdited();
      }}
      includeCover={includeCover}
      onIncludeCoverChange={(value) => {
        setIncludeCover(value);
        markEdited();
      }}
      coverFileOnTemplate={Boolean(activeTemplate?.coverStoragePath)}
      coverTitle={coverTitle}
      onCoverTitleChange={(value) => {
        setCoverTitle(value);
        markEdited();
      }}
      coverBody={coverBody}
      onCoverBodyChange={(value) => {
        setCoverBody(value);
        markEdited();
      }}
      resolvedLetterhead={resolvedLetterhead}
      pdfStale={pdfStale}
      pdfReady={Boolean(pdfPath) && !pdfStale}
    />
  );

  return (
    <div className="flex flex-col gap-s4">
      <div className="flex flex-wrap items-center gap-s3">
        <Button variant="tertiary" onClick={requestLeave}>
          {t('patientReports.back')}
        </Button>
        <StatusChip tone={status === 'finished' ? 'completed' : 'neutral'}>
          {status === 'finished'
            ? t('patientReports.status_finished')
            : t('patientReports.status_draft')}
        </StatusChip>
        {!locked ? (
          <Button variant="secondary" loading={save.isPending} onClick={() => void onSave()}>
            {t('patientReports.save')}
          </Button>
        ) : null}
        {!locked ? (
          <Button loading={finish.isPending} onClick={() => void onFinish()}>
            {t('patientReports.finish')}
          </Button>
        ) : null}
        {!locked && reportId ? (
          <Button
            variant="tertiary"
            loading={removeDraft.isPending}
            onClick={() => {
              if (!window.confirm(t('patientReports.delete_confirm'))) return;
              void removeDraft.mutateAsync(reportId).then(
                () => {
                  toast.success(t('patientReports.deleted'));
                  onClose();
                },
                () => toast.error(t('patientReports.delete_error')),
              );
            }}
          >
            {t('patientReports.delete_draft')}
          </Button>
        ) : null}
        {!locked && wordTemplatePath ? (
          <Button variant="secondary" loading={wordBusy} onClick={() => void onDownloadWord()}>
            {t('patientReports.download_word')}
          </Button>
        ) : null}
      </div>

      {locked ? reportSettings : null}

      {locked && pdfPath ? <StoredReportPdf path={pdfPath} /> : null}

      {locked ? null : (
        <div ref={tools.ref} className={tools.className} style={tools.style}>
          <ReportToolColumn
            wide={tools.wide}
            edge="start"
            open={tools.sources.open}
            onOpenChange={tools.sources.setOpen}
            title={t('patientReports.sources')}
            hint={t('patientReports.sources_hint')}
            icon={<Library className="h-4 w-4" strokeWidth={1.75} aria-hidden />}
            collapseLabel={t('patientReports.sources_collapse')}
            resizeLabel={t('patientReports.sources_resize')}
            width={tools.sources.width}
            min={REPORT_TOOL_WIDTH_MIN}
            max={tools.sources.max}
            onWidthChange={tools.sources.setWidth}
            onWidthReset={tools.sources.resetWidth}
            testId="report-sources"
            stackClassName="rounded-ctl border border-ds-line bg-ds-card p-s4"
            bodyClassName="mt-s3"
          >
            <div className="flex flex-col gap-s4">
              <ReportTextLibrary
                types={library.types}
                texts={library.texts}
                loading={library.isLoading}
                error={Boolean(library.error)}
                busy={library.isSaving}
                disabled={busy}
                usedTypeIds={
                  new Set(
                    sections.flatMap((section) =>
                      section.kind === 'user_text' && section.typeId ? [section.typeId] : [],
                    ),
                  )
                }
                canManageType={library.canManageType}
                onCreate={async (input) => {
                  const type = await library.createType(input.name);
                  if (input.body.trim()) {
                    try {
                      await library.saveText({ typeId: type.id, body: input.body });
                    } catch (error) {
                      await library.deleteType(type.id).catch(() => undefined);
                      throw error;
                    }
                  }
                  toast.success(t('patientReports.texts_saved'));
                }}
                onRename={async (input) => {
                  await library.renameType(input);
                  toast.success(t('patientReports.texts_saved'));
                }}
                onDeleteType={async (id) => {
                  await library.deleteType(id);
                  toast.success(t('patientReports.texts_type_deleted'));
                }}
                onSaveText={async (input) => {
                  await library.saveText(input);
                  toast.success(t('patientReports.texts_saved'));
                }}
                onDeleteText={async (id) => {
                  await library.deleteText(id);
                  toast.success(t('patientReports.texts_deleted'));
                }}
                onInsert={(type) => {
                  const next = insertUserTextSection(sections, type.id, type.name);
                  if (next === sections) return;
                  setSections(next);
                  markEdited();
                  setToolTab('content');
                  toast.success(t('patientReports.texts_inserted'));
                }}
              />
            </div>
          </ReportToolColumn>

          {wordTemplatePath ? (
            <WordReportPreview
              clinicId={clinicId}
              wordPath={wordTemplatePath}
              coverPath={includeCover ? (activeTemplate?.coverStoragePath ?? null) : null}
              coverMime={activeTemplate?.coverMime ?? null}
              includeCover={includeCover}
              letterhead={letterhead}
              context={reportPlaceholderContext({
                reportTitle: preview.title,
                authorName,
                clinicName: clinic.data?.name ?? '',
                generatedAt: new Date(),
                patient: catalog.patient,
                cprAccess,
                cpr,
                cprHidden: previewText.cprHidden,
                cprLoading: previewText.cprLoading,
                coverTitle,
                coverBody,
                sections,
                model: preview,
              })}
              blocks={previewToPdfBlocks(preview)}
              patientId={patientId}
              canRevealCpr={canRevealCpr}
              fallback={
                <ReportPreview
                  model={preview}
                  letterheadLabel={t(`patientReports.letterhead_${resolvedLetterhead}`)}
                />
              }
            />
          ) : (
            <ReportPreview
              model={preview}
              letterheadLabel={t(`patientReports.letterhead_${resolvedLetterhead}`)}
            />
          )}

          <ReportToolColumn
            wide={tools.wide}
            edge="end"
            open={tools.document.open}
            onOpenChange={tools.document.setOpen}
            title={t('patientReports.document')}
            icon={<FileText className="h-4 w-4" strokeWidth={1.75} aria-hidden />}
            collapseLabel={t('patientReports.document_collapse')}
            resizeLabel={t('patientReports.document_resize')}
            width={tools.document.width}
            min={REPORT_TOOL_WIDTH_MIN}
            max={tools.document.max}
            onWidthChange={tools.document.setWidth}
            onWidthReset={tools.document.resetWidth}
            testId="report-document"
            stackClassName="rounded-ctl border border-ds-line bg-ds-sunk p-s4"
          >
            <ReportToolTabs value={toolTab} onChange={setToolTab} />
            {toolTab === 'settings' ? (
              <div
                role="tabpanel"
                id="report-tool-settings-panel"
                aria-labelledby="report-tool-settings"
                className="mt-s3"
              >
                {reportSettings}
              </div>
            ) : (
              <div
                role="tabpanel"
                id="report-tool-content-panel"
                aria-labelledby="report-tool-content"
                className="mt-s3"
              >
                <p className={cn(TYPE.small, 'mb-s3 text-ds-muted')}>
                  {t('patientReports.overview_hint')}
                </p>
                <ReportSectionCards
                  sections={sections}
                  tags={[...(catalog.highlightTags ?? []), ...(templates.data?.tags ?? [])].filter(
                    (tag, index, all) => all.findIndex((item) => item.id === tag.id) === index,
                  )}
                  questions={questions}
                  formOptions={formOptions}
                  patientQuestionnaires={catalog.questionnaires}
                  journalNotes={catalog.journalNotes}
                  markings={catalog.markings}
                  files={catalog.files}
                  textTypes={library.types}
                  onCreateTextType={async (name) => {
                    try {
                      const type = await library.createType(name);
                      toast.success(t('patientReports.texts_saved'));
                      return type.id;
                    } catch (caught) {
                      toast.error(
                        patientReportTextErrorMessage(
                          caught,
                          t('patientReports.texts_save_error'),
                          {
                            duplicate: t('patientReports.texts_duplicate'),
                            cpr: t('patientReports.texts_cpr_blocked'),
                          },
                        ),
                      );
                      return null;
                    }
                  }}
                  missingFormIds={missingQuestionnaireEntries(sections, catalog).flatMap((form) => [
                    form.id,
                    form.name,
                  ])}
                  disabled={busy}
                  onChange={(next) => {
                    setSections(next);
                    markEdited();
                  }}
                />
              </div>
            )}
          </ReportToolColumn>
        </div>
      )}
      <Modal
        open={leaveOpen}
        title={t('patientReports.leave_title')}
        description={t('patientReports.leave_body')}
        onClose={() => setLeaveOpen(false)}
        tone="warning"
        showIcon
        role="alertdialog"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setLeaveOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setLeaveOpen(false);
                onClose();
              }}
            >
              {t('common.no')}
            </Button>
            <Button type="button" loading={save.isPending} onClick={() => void saveAndLeave()}>
              {t('common.yes')}
            </Button>
          </>
        }
      >
        {null}
      </Modal>
    </div>
  );
}

function ReportEditorSettings({
  locked,
  title,
  templates,
  templateId,
  onTemplateChange,
  letterhead,
  onLetterheadChange,
  includeCover,
  onIncludeCoverChange,
  coverFileOnTemplate,
  coverTitle,
  onCoverTitleChange,
  coverBody,
  onCoverBodyChange,
  resolvedLetterhead,
  pdfStale,
  pdfReady,
}: {
  locked: boolean;
  title: string;
  templates: readonly { id: string; name: string }[];
  templateId: string | null;
  onTemplateChange: (templateId: string) => void;
  letterhead: PatientReportLetterheadChoice;
  onLetterheadChange: (value: PatientReportLetterheadChoice) => void;
  includeCover: boolean;
  onIncludeCoverChange: (value: boolean) => void;
  coverFileOnTemplate: boolean;
  coverTitle: string;
  onCoverTitleChange: (value: string) => void;
  coverBody: string;
  onCoverBodyChange: (value: string) => void;
  resolvedLetterhead: PatientReportLetterheadMode;
  pdfStale: boolean;
  pdfReady: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex max-w-xl flex-col gap-s3">
      <Select
        id="report-letterhead"
        label={t('patientReports.letterhead_label')}
        value={letterhead}
        disabled={locked}
        onChange={(event) => {
          if (locked) return;
          onLetterheadChange(event.target.value as PatientReportLetterheadChoice);
        }}
      >
        <option value="inherit">{t('patientReports.letterhead_inherit')}</option>
        <option value="none">{t('settings.integrations.patient_reports.letterhead_none')}</option>
        <option value="invoice">
          {t('settings.integrations.patient_reports.letterhead_invoice')}
        </option>
        <option value="custom">
          {t('settings.integrations.patient_reports.letterhead_custom')}
        </option>
      </Select>
      <Select
        id="report-template"
        label={t('patientReports.template_label')}
        value={templateId ?? ''}
        disabled={locked}
        onChange={(event) => {
          if (locked) return;
          onTemplateChange(event.target.value);
        }}
      >
        <option value="">{t('patientReports.template_blank')}</option>
        {templates.map((template) => (
          <option key={template.id} value={template.id}>
            {template.name}
          </option>
        ))}
      </Select>
      <label className="flex items-start gap-s2">
        <input
          type="checkbox"
          className="mt-1"
          checked={includeCover}
          disabled={locked}
          onChange={(event) => {
            if (locked) return;
            onIncludeCoverChange(event.target.checked);
          }}
        />
        <span>
          <span className={cn(TYPE.body, 'text-ds-text')}>{t('patientReports.preview_cover')}</span>
          <span className={cn(TYPE.small, 'mt-s1 block text-ds-muted')}>
            {t('patientReports.preview_cover_hint')}
          </span>
        </span>
      </label>
      <p className={cn(TYPE.small, 'text-ds-muted')}>{t('patientReports.cover_hint')}</p>
      {includeCover && coverFileOnTemplate ? (
        <p className={cn(TYPE.small, 'text-ds-muted')}>
          {t('patientReports.cover_file_on_report')}
        </p>
      ) : null}
      {includeCover ? (
        <div className="flex flex-col gap-s3">
          <Input
            id="report-cover-title"
            label={t('patientReports.cover_title')}
            value={coverTitle}
            disabled={locked}
            onChange={(event) => onCoverTitleChange(event.target.value)}
          />
          <Input
            id="report-cover-body"
            label={t('patientReports.cover_body')}
            value={coverBody}
            disabled={locked}
            onChange={(event) => onCoverBodyChange(event.target.value)}
          />
        </div>
      ) : null}
      <div>
        <p className={cn(TYPE.small, 'font-medium text-ds-text')}>
          {t('patientReports.report_title_label')}
        </p>
        <p className={cn(TYPE.body, 'mt-s1 text-ds-text')} data-testid="report-settings-title">
          {title || t('patientReports.untitled')}
        </p>
        <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>
          {t('patientReports.report_title_from_template')}
        </p>
      </div>
      <p className={cn(TYPE.small, 'text-ds-muted')}>
        {locked
          ? t('patientReports.locked')
          : `${t(`patientReports.letterhead_${resolvedLetterhead}`)} ${t('patientReports.override_hint')}${pdfStale ? ` ${t('patientReports.pdf_stale')}` : ''}${pdfReady ? ` ${t('patientReports.pdf_ready')}` : ''}`}
      </p>
    </div>
  );
}

function StoredReportPdf({ path }: { path: string }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let objectUrl = '';
    let cancelled = false;
    void loadPatientReportPdfBlob(path)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);
  if (failed)
    return (
      <p className={cn(TYPE.small, 'text-ds-red-ink')}>{t('patientReports.download_error')}</p>
    );
  if (!url)
    return <p className={cn(TYPE.small, 'text-ds-muted')}>{t('patientReports.locked_loading')}</p>;
  return <ReportPreviewFrame title={t('patientReports.locked')} src={url} />;
}

function defaultTitle(): string {
  return `Rapport — ${new Date().toLocaleDateString('da-DK')}`;
}

function reportEditorSnapshot(input: {
  title: string;
  templateId: string | null;
  letterhead: PatientReportLetterheadChoice;
  includeCover: boolean;
  coverTitle: string;
  coverBody: string;
  sections: readonly ReportSection[];
}): string {
  return JSON.stringify({
    title: input.title.trim(),
    templateId: input.templateId,
    letterhead: input.letterhead,
    includeCover: input.includeCover,
    coverTitle: input.coverTitle,
    coverBody: input.coverBody,
    sections: input.sections,
  });
}

function previewLabels(t: (key: string) => string): ReportPreviewLabels {
  return {
    name: t('patientReports.field_name'),
    cpr: t('patientReports.field_cpr'),
    email: t('patientReports.field_email'),
    phone: t('patientReports.field_phone'),
    address: t('patientReports.field_address'),
    jobTitle: t('patientReports.field_job'),
    cave: t('patientReports.field_cave'),
    notes: t('patientReports.field_notes'),
    diagnosis: t('patientReports.field_diagnosis'),
    patNumber: t('patientReports.field_pat_number'),
    locale: t('patientReports.field_locale'),
    notRecorded: t('patientReports.not_recorded'),
    cprHidden: t('patientReports.cpr_hidden'),
    cprLoading: t('patientReports.cpr_loading'),
    comment: t('patientReports.field_comment'),
    file: t('patientReports.kind_file'),
    page: t('patientReports.page'),
    quote: t('patientReports.field_quote'),
    score: t('patientReports.field_score'),
    answer: t('patientReports.field_answer'),
    missingQuestionnaire: t('patientReports.missing_questionnaire'),
    chooseQuestionnaire: t('patientReports.form_none'),
    chooseJournal: t('patientReports.journal_pick'),
    chooseMarking: t('patientReports.marking_pick'),
    formMissing: t('patientReports.form_missing'),
  };
}
